import React, { FC, useRef, useState } from 'react';
import translate from '@translate';
import ProductsSelect from 'src/components/ProductsSelect';
import { checkFormValidity } from 'src/utils/formFunctions';
import TextField from 'src/components/TextField';
import Grid, { GridItem } from 'src/components/Grid';
import Button from 'src/components/Button';
import Typography from 'src/components/Typography';
import { colors, sizes } from 'src/themes/default';
import { ClaimProductType } from '../../types/claim';
import { FormErrorsType } from '../../types/global';

export type EditKeysType = keyof ClaimProductType;

const initData: ClaimProductType = {
    LINE_STATE: 'E'
};
type NewProductBlockPropsType = {
    data?: ClaimProductType[];
    onChange: (value: any) => void;
    disableEdit?: boolean;
    editErrors: FormErrorsType;
    skladonUser?: number;
    canEditProducts: boolean;
};

const NewProductBlock: FC<NewProductBlockPropsType> = (props) => {
    const formRef = useRef<HTMLFormElement>(null);

    const [editData, setEditData] = useState<ClaimProductType>(initData);
    const [editErrors, setEditErrors] = useState<FormErrorsType>({});

    const changeValue = (key: EditKeysType, value: any, error: boolean) => {
        const newData = { ...editData };
        if (key === 'PRODUCT_ID') {
            newData.PRODUCT_ID = value?.ID;
            newData.NAME = value?.NAME;
            newData.SKU = value?.SKU;
        } else {
            newData[key] = value;
        }
        const newErrors = { ...editErrors };
        newErrors[key] = error;
        setEditData(newData);
        setEditErrors(newErrors);
    };

    const handleAddRow = () => {
        const newFormErrors = checkFormValidity(formRef);

        if (!Object.keys(newFormErrors).length) {
            const newId = Math.min.apply(0, [0, ...(props.data || []).map((item: any): any => item.ID)]) - 1;

            props.onChange([
                ...(props.data || []),
                {
                    ...editData,
                    ID: newId
                }
            ]);

            setEditData(initData);
        }

        setEditErrors(newFormErrors);
    };

    return (
        <>
            <Typography
                gutterBottom
                variant="h3"
                sx={props.editErrors.PRODUCTS ? { color: colors.alert_O } : {}}
            >
                {translate('claimDetail.products')}
            </Typography>
            {!props.disableEdit && props.canEditProducts && (
                <form ref={formRef}>
                    <Grid>
                        <GridItem cols={6}>
                            <ProductsSelect
                                value={editData.PRODUCT_ID}
                                error={editErrors.PRODUCT_ID}
                                idKey="ID"
                                onChange={(value, error) => changeValue('PRODUCT_ID', value, error)}
                                name="PRODUCT_ID"
                                label={translate('claimDetail.productTable.nameSku')}
                                fullWidth
                                disabled={props.disableEdit}
                                required
                            />
                        </GridItem>
                        <GridItem cols={2}>
                            <TextField
                                value={editData.CLAIMED_QUANTITY}
                                error={editErrors.CLAIMED_QUANTITY}
                                onChange={(value, error) => changeValue('CLAIMED_QUANTITY', value, error)}
                                name="CLAIMED_QUANTITY"
                                label={translate('claimDetail.productTable.claimedQuantity')}
                                fullWidth
                                required
                                type="numberLocale"
                                min={1}
                                step="1"
                                disabled={props.disableEdit}
                            />
                        </GridItem>
                        {props.skladonUser ?
                            <GridItem cols={2}>
                                <TextField
                                    value={editData.RESEND_QUANTITY}
                                    error={editErrors.RESEND_QUANTITY}
                                    onChange={(value, error) => changeValue('RESEND_QUANTITY', value || null, error)}
                                    name="RESEND_QUANTITY"
                                    label={translate('claimDetail.productTable.resendQuantity')}
                                    fullWidth
                                    type="numberLocale"
                                    min={0}
                                    step="1"
                                    disabled={props.disableEdit}
                                />
                            </GridItem> : null
                        }
                        <GridItem cols={2}>
                            <Button
                                onClick={handleAddRow}
                                disabled={props.disableEdit}
                                sx={{ marginTop: sizes.input_top_PD }}
                                mobileFullWidht
                            >
                                + {translate('returnDetail.productTable.action.add')}
                            </Button>
                        </GridItem>
                    </Grid>
                </form>
            )}
        </>
    );
};

export default NewProductBlock;
